import React from "react";
import { Button, Col, DatePicker, Form, Input, Row, Select } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { NamePath } from "antd/lib/form/interface";
import { Dayjs } from "dayjs";

// Constants
import { CarLocationServices } from "src/constants";

// Helpers
import { filterOptions, validateDateTimeRange } from "src/helpers/form.helper";

// Services
import { disableDate } from "src/services/booking.service";

// Components
import CarSearchTimePicker from "src/components/booking/CarSearchTimePicker";

type Props = {
    locationOptions: SelectOption[];
};

const DifferentLocationsForm = ({ locationOptions }: Props) => {
    const intl = useIntl();
    const rules = {
        pickUpLocation: [
            {
                required: true,
                message: intl.formatMessage(
                    { id: "form.error.xRequierd" },
                    {
                        name: intl.formatMessage({
                            id: "carSearchCard.carPickUpLocation",
                        }),
                    }
                ),
            },
        ],
        returnLocation: [
            {
                required: true,
                message: intl.formatMessage(
                    { id: "form.error.xRequierd" },
                    {
                        name: intl.formatMessage({
                            id: "carSearchCard.carReturnLocation",
                        }),
                    }
                ),
            },
        ],
        pickUpDate: [
            {
                required: true,
                message: intl.formatMessage(
                    { id: "form.error.xRequierd" },
                    {
                        name: intl.formatMessage({
                            id: "carSearchCard.carPickUpDate.error.date.required",
                        }),
                    }
                ),
            },
        ],
        pickUpTime: [
            {
                required: true,
                message: intl.formatMessage(
                    { id: "form.error.xRequierd" },
                    {
                        name: intl.formatMessage({
                            id: "carSearchCard.carPickUpDate.error.time.required",
                        }),
                    }
                ),
            },
        ],
        returnDate: [
            {
                required: true,
                message: intl.formatMessage(
                    { id: "form.error.xRequierd" },
                    {
                        name: intl.formatMessage({
                            id: "carSearchCard.carReturnDate.error.date.required",
                        }),
                    }
                ),
            },
            ({
                getFieldValue,
            }: {
                getFieldValue: (name: NamePath) => any;
            }) => ({
                validator(_: any, value: Dayjs) {
                    const pickUpDate: Dayjs = getFieldValue([
                        "locations",
                        CarLocationServices.DIFFERENT_LOCATIONS,
                        "pickUpDate",
                    ]);
                    const pickUpTime: string = getFieldValue([
                        "locations",
                        CarLocationServices.DIFFERENT_LOCATIONS,
                        "pickUpTime",
                    ]);
                    const returnTime: string = getFieldValue([
                        "locations",
                        CarLocationServices.DIFFERENT_LOCATIONS,
                        "returnTime",
                    ]);

                    if (
                        validateDateTimeRange(
                            pickUpDate,
                            pickUpTime,
                            value,
                            returnTime
                        )
                    ) {
                        return Promise.resolve();
                    }
                    return Promise.reject(
                        new Error(
                            intl.formatMessage(
                                { id: "form.error.mustBeAfter" },
                                {
                                    name: intl.formatMessage({
                                        id: "carSearchCard.carPickUpDate",
                                    }),
                                }
                            )
                        )
                    );
                },
            }),
        ],
        returnTime: [
            {
                required: true,
                message: intl.formatMessage(
                    { id: "form.error.xRequierd" },
                    {
                        name: intl.formatMessage({
                            id: "carSearchCard.carReturnDate.error.time.required",
                        }),
                    }
                ),
            },
        ],
    };

    return (
        <DivStyled className="different-locations-form">
            <Row>
                <Col
                    xs={24}
                    sm={10}
                    md={8}
                    lg={9}
                    xl={7}
                    xxl={5}
                    className="location-column pick-up"
                >
                    <Form.Item
                        name={[
                            "locations",
                            CarLocationServices.DIFFERENT_LOCATIONS,
                            "pickUpLocation",
                        ]}
                        label={intl.formatMessage({
                            id: "carSearchCard.carPickUpLocation",
                        })}
                        rules={rules.pickUpLocation}
                    >
                        <Select
                            showSearch
                            placeholder={intl.formatMessage({
                                id: "carSearchCard.selectLocation",
                            })}
                            optionFilterProp="children"
                            filterOption={filterOptions}
                            options={locationOptions}
                        />
                    </Form.Item>
                </Col>
                <Col
                    xs={24}
                    sm={14}
                    md={12}
                    lg={9}
                    xl={7}
                    xxl={5}
                    className="date-time-column pick-up"
                >
                    <Form.Item
                        label={intl.formatMessage({
                            id: "carSearchCard.carPickUpDate",
                        })}
                    >
                        <Input.Group className="date-time-group">
                            <Form.Item
                                name={[
                                    "locations",
                                    CarLocationServices.DIFFERENT_LOCATIONS,
                                    "pickUpDate",
                                ]}
                                rules={rules.pickUpDate}
                                noStyle
                            >
                                <DatePicker
                                    allowClear={false}
                                    format="DD/MM/YY"
                                    disabledDate={disableDate}
                                />
                            </Form.Item>
                            <Form.Item
                                name={[
                                    "locations",
                                    CarLocationServices.DIFFERENT_LOCATIONS,
                                    "pickUpTime",
                                ]}
                                rules={rules.pickUpTime}
                                noStyle
                            >
                                <CarSearchTimePicker />
                            </Form.Item>
                        </Input.Group>
                    </Form.Item>
                </Col>
                <Col xs={0} md={4} lg={6} xl={10} xxl={0}></Col>
                <Col
                    xs={24}
                    sm={10}
                    md={8}
                    lg={9}
                    xl={7}
                    xxl={5}
                    className="location-column return"
                >
                    <Form.Item
                        name={[
                            "locations",
                            CarLocationServices.DIFFERENT_LOCATIONS,
                            "returnLocation",
                        ]}
                        label={intl.formatMessage({
                            id: "carSearchCard.carReturnLocation",
                        })}
                        rules={rules.returnLocation}
                    >
                        <Select
                            showSearch
                            placeholder={intl.formatMessage({
                                id: "carSearchCard.selectLocation",
                            })}
                            optionFilterProp="children"
                            filterOption={filterOptions}
                            options={locationOptions}
                        />
                    </Form.Item>
                </Col>
                <Col
                    xs={24}
                    sm={14}
                    md={12}
                    lg={9}
                    xl={7}
                    xxl={5}
                    className="date-time-column return"
                >
                    <Form.Item
                        label={intl.formatMessage({
                            id: "carSearchCard.carReturnDate",
                        })}
                    >
                        <Input.Group className="date-time-group">
                            <Form.Item
                                name={[
                                    "locations",
                                    CarLocationServices.DIFFERENT_LOCATIONS,
                                    "returnDate",
                                ]}
                                rules={rules.returnDate}
                                noStyle
                                dependencies={[
                                    [
                                        "locations",
                                        CarLocationServices.DIFFERENT_LOCATIONS,
                                        "pickUpDate",
                                    ],
                                    [
                                        "locations",
                                        CarLocationServices.DIFFERENT_LOCATIONS,
                                        "pickUpTime",
                                    ],
                                    [
                                        "locations",
                                        CarLocationServices.DIFFERENT_LOCATIONS,
                                        "returnTime",
                                    ],
                                ]}
                            >
                                <DatePicker
                                    allowClear={false}
                                    format="DD/MM/YY"
                                    disabledDate={disableDate}
                                />
                            </Form.Item>
                            <Form.Item
                                name={[
                                    "locations",
                                    CarLocationServices.DIFFERENT_LOCATIONS,
                                    "returnTime",
                                ]}
                                rules={rules.returnTime}
                                noStyle
                            >
                                <CarSearchTimePicker />
                            </Form.Item>
                        </Input.Group>
                    </Form.Item>
                </Col>
                <Col xs={24} md={4} lg={6} xl={10} xxl={4} className="action">
                    <Button type="primary" htmlType="submit">
                        <FormattedMessage id="carSearchCard.findCar" />
                    </Button>
                </Col>
            </Row>
        </DivStyled>
    );
};

const DivStyled = styled.div`
    &.different-locations-form {
        .ant-form-item {
            margin-bottom: 20px;
            @media screen and (max-width: 767px) {
                margin-bottom: 10px;
            }
        }

        .date-time-group {
            display: inline-flex;
            justify-content: space-between;

            .ant-picker {
                width: inherit;
            }

            .car-search-time-picker {
                margin-left: 10px;

                @media screen and (max-width: 575px) {
                    margin-left: 20px;
                }
            }
        }

        .location-column {
            padding-right: 12px;

            &.return {
                @media screen and (min-width: 1600px) {
                    padding-left: 30px;
                }
            }

            @media screen and (max-width: 1599px) {
                padding-right: 15px;
            }

            @media screen and (max-width: 767px) {
                padding-right: 6px;
            }

            @media screen and (max-width: 575px) {
                padding-right: 0;
            }

            .ant-select {
                max-width: 320px;
            }
        }

        .date-time-column {
            padding-left: 15px;
            padding-right: 6px;

            &.pick-up {
                @media screen and (min-width: 1600px) {
                    padding-left: 0;
                    padding-right: 0;
                }
            }

            &.return {
                @media screen and (min-width: 1600px) {
                    padding-left: 0;
                    padding-right: 0;
                }
            }

            @media screen and (max-width: 767px) {
                padding-left: 6px;
            }

            @media screen and (max-width: 575px) {
                padding-left: 0;
                padding-right: 0;
            }
        }

        .action {
            padding-left: 6px;
            margin-top: 30px;
            text-align: right;

            @media screen and (min-width: 1600px) {
                padding-left: 12px;
            }

            @media screen and (max-width: 767px) {
                margin-top: 10px;
                padding-left: 0;
            }

            .ant-btn {
                border-radius: 2px;
                width: 100%;
                max-width: 168px;

                @media screen and (max-width: 767px) {
                    max-width: unset;
                }
            }
        }
    }
`;

export default DifferentLocationsForm;
